import { useState, useEffect, useContext, JSX } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { PATHS } from 'src/common/constants';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { selectPropertyWalkThroughFormIsVisible } from 'src/store/selectors/propertyWalkThroughForm';
import { serviceRequestTableLocalTime } from 'src/helpers/validationHelper';
import { store } from 'src/store';
import { BrandingContext } from 'src/branding/BrandingContextProvider';
import { hasHomeOwnerVisibleNote } from 'src/SharedCustomHooks/UseFaqsNotesDocumentsAPI/helpers';
import { UseNotesAPIReturn } from 'src/SharedCustomHooks/UseNotesAPI/useNotesAPI';
import {
  WalkThrough,
  Property,
  Suite as SuiteType,
  Counts,
  Contact,
} from 'src/SharedCustomHooks/UseSuiteActions/types';
import { ModulePermission } from 'src/components/ContactForm/types';
import { SuiteDetailsComponents } from 'src/pages/Suite/Components/SuiteDetails/SuiteDetailsComponents';
import { buildSuiteDetailsSkeleton } from 'src/pages/Suite/Components/SuiteDetails/skeletons/SuiteDetailsSkeleton';
import {
  useSuitePageData,
  UseSuitePageDataReturn,
} from 'src/pages/Suite/CustomHooks/UseSuitePageData/useSuitePageData';
import {
  useSuitePageUIState,
  UseSuitePageUIStateReturn,
} from 'src/pages/Suite/CustomHooks/useSuitePageUIState';
import {
  ADMIN_LINK_VIEW_ROLES,
  calculateWarrantyDaysRemaining,
  computeActiveTabIndex,
  isHomeOwnerCheckUser,
  isHomeOwnerOccupantUser,
  shouldShowHomeownerFeedback,
} from 'src/pages/Suite/CustomHooks/UseSuitePageOrchestrator/helpers';

export interface UseSuitePageOrchestratorParams {
  match: { params: { id: string } };
  location: any;
  history: any;
}

interface SuitePageBootstrapSlice {
  loadingSuite: boolean;
  error: string | null;
  isLoading: boolean;
}

interface SuitePageDataSlice {
  suite: SuiteType | null;
  counts: Counts | null;
  walkThroughs: WalkThrough[];
  properties: Property[];
  property: Property | null;
  propertyManagerDetails: Contact | null;
  allowedTabList: ModulePermission[];
  notesAPI: UseNotesAPIReturn;
}

interface SuitePageUIStateSlice {
  showOverlay: boolean;
  isEventShow: boolean;
  moreDetails: any | null;
}

interface SuitePageReduxSlice {
  propertyWalkThroughFormIsVisible: boolean;
}

interface SuitePageContextSlice {
  user: any;
  userRole: any;
  branding: any;
  serviceRequestId: string | null;
  suiteIdParts: string[];
  showHomeownerDashboardNavigation: boolean;
  isFromDashboard: boolean;
}

interface SuitePageDerivationsSlice {
  noteVisibleToHomeownerOccupantRealtor: boolean;
  isHomeOwnerCheckUserResult: boolean;
  isHomeOwnerOccupantUserResult: boolean;
  activeTabIndex: number;
  suiteDetailsComponents: JSX.Element[];
}

interface SuitePageHandlersSlice {
  onSaveSuite: (
    suiteData: any,
    suiteAreas: any,
    floorPlanImage: any
  ) => Promise<{ ok: boolean }>;
  onDeleteUnit: (unitId: string) => Promise<void>;
  onReRenderKeyPickupPdf: () => Promise<void>;
  onToggleMobileSync: () => void;
  onFetchDetails: () => void;
  onEditSuite: () => void;
  onCloseOverlay: () => void;
  onCloseMoreDetails: () => void;
  fetchSuiteData: () => Promise<void>;
  refreshSuiteCounts: () => Promise<void>;
}

export interface UseSuitePageOrchestratorReturn
  extends SuitePageBootstrapSlice,
    SuitePageDataSlice,
    SuitePageUIStateSlice,
    SuitePageReduxSlice,
    SuitePageContextSlice,
    SuitePageDerivationsSlice,
    SuitePageHandlersSlice {}

export const useSuitePageOrchestrator = ({
  match,
  location,
  history,
}: UseSuitePageOrchestratorParams): UseSuitePageOrchestratorReturn => {
  const { branding } = useContext(BrandingContext);

  const authenticatedUser: any = useSelector(selectAuthenticatedUser);
  const userRole: any = authenticatedUser.roles[0]?.role?.name;
  const user: any = authenticatedUser;

  const propertyWalkThroughFormIsVisible: boolean = useSelector(
    selectPropertyWalkThroughFormIsVisible
  );

  const data: UseSuitePageDataReturn = useSuitePageData({
    match,
    location,
    history,
    branding,
    user,
    userRole,
  });

  const uiState: UseSuitePageUIStateReturn = useSuitePageUIState();

  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const serviceRequestId: string | null =
    location.state?.data?.serviceRequestId || null;

  const { orders } = store.getState().onboardingData;
  const showHomeownerDashboardNavigation: boolean =
    Object.keys(orders).length > 0;

  const currentTheme: string | null = localStorage.getItem('theme');
  const currentDay: any = serviceRequestTableLocalTime(moment(), 'suitepage');

  const initializeComponent = async (): Promise<void> => {
    try {
      setIsInitialLoading(true);
      setError(null);

      uiState.markCalendlyEventShown(
        window.location.href.includes('openCalendly')
      );

      if (shouldShowHomeownerFeedback(user)) {
        uiState.markFeedbackPopupVisible();
      }

      await data.fetchSuiteData();
      await data.fetchServiceRequestData();
    } catch (err: any) {
      console.error('Component initialization failed:', err);
      setError(err.message || 'Failed to initialize component');
    } finally {
      setIsInitialLoading(false);
    }
  };

  useEffect(() => {
    void initializeComponent();
  }, []);

  const onEditSuite = (): void => {
    if (!data.suite) {
      console.warn('onEditSuite called with no suite loaded');
      return;
    }

    uiState.openEditOverlay();
  };

  const onFetchDetails = (): void => {
    if (!data.suite) {
      console.warn('onFetchDetails called with no suite loaded');
      return;
    }

    history.push(
      PATHS.KEY_PICKUP_VIEW.replace(
        ':id',
        `${data.suite.propertyId}&${data.suite.id}`
      )
    );
  };

  const onFetchMoreDetails = async (): Promise<void> => {
    const details: any = await data.fetchMoreDetails();

    if (details) {
      uiState.showMoreDetails(details);
    }
  };

  const noteVisibleToHomeownerOccupantRealtor: boolean =
    hasHomeOwnerVisibleNote(data.notesAPI.notes);

  const isHomeOwnerCheckUserResult: boolean = isHomeOwnerCheckUser(user);
  const isHomeOwnerOccupantUserResult: boolean = isHomeOwnerOccupantUser(user);
  const isFromDashboard: boolean = location?.state?.from === 'Dashboard';

  const hasLinkViewPermission: boolean = ADMIN_LINK_VIEW_ROLES.includes(
    user.roles[0]?.role?.name
  );

  const parkingDetailsTab: ModulePermission | undefined =
    data.allowedTabList.find(
      (tabItem: ModulePermission) => tabItem.name === 'Parking Details'
    );

  const isHomeOwnerRealtorUser: boolean =
    !parkingDetailsTab &&
    ['Home Owner', 'Realtor'].includes(user.roles[0]?.role?.name);

  const activeTabIndex: number = computeActiveTabIndex(
    location,
    data.walkThroughs
  );

  const oneYearWarrantyDaysRemaining: number = data.suite?.assetWarrantyOneYear
    ? calculateWarrantyDaysRemaining(
        data.suite.assetWarrantyOneYear,
        currentDay
      )
    : 0;

  const twoYearWarrantyDaysRemaining: number = data.suite?.assetWarrantyTwoYear
    ? calculateWarrantyDaysRemaining(
        data.suite.assetWarrantyTwoYear,
        currentDay
      )
    : 0;

  const suiteDetailsComponents: JSX.Element[] = data.suite
    ? SuiteDetailsComponents({
        suite: data.suite,
        counts: data.counts,
        currentTheme,
        oneYearWarrantyDaysRemaining,
        twoYearWarrantyDaysRemaining,
        isHomeOwnerUser: isHomeOwnerRealtorUser,
        hasLinkViewPermission,
        isFromDashboard,
        feedBackReportPopup: uiState.feedBackReportPopup,
        onFetchMoreDetails,
        refreshKeyDatesTrigger: data.refreshKeyDatesTrigger,
        showKeyDates: uiState.showKeyDates,
        onKeyDatesEmpty: uiState.hideKeyDates,
      })
    : buildSuiteDetailsSkeleton();

  const loadingSuite: boolean = isInitialLoading || !data.suite;

  const bootstrap: SuitePageBootstrapSlice = {
    loadingSuite,
    error,
    isLoading: data.isMutating,
  };

  const dataSlice: SuitePageDataSlice = {
    suite: data.suite,
    counts: data.counts,
    walkThroughs: data.walkThroughs,
    properties: data.properties,
    property: data.property,
    propertyManagerDetails: data.propertyManagerDetails,
    allowedTabList: data.allowedTabList,
    notesAPI: data.notesAPI,
  };

  const uiSlice: SuitePageUIStateSlice = {
    showOverlay: uiState.showOverlay,
    isEventShow: uiState.isEventShow,
    moreDetails: uiState.moreDetails,
  };

  const reduxSlice: SuitePageReduxSlice = {
    propertyWalkThroughFormIsVisible,
  };

  const contextSlice: SuitePageContextSlice = {
    user,
    userRole,
    branding,
    serviceRequestId,
    suiteIdParts: data.suiteIdParts,
    showHomeownerDashboardNavigation,
    isFromDashboard,
  };

  const derivationsSlice: SuitePageDerivationsSlice = {
    noteVisibleToHomeownerOccupantRealtor,
    isHomeOwnerCheckUserResult,
    isHomeOwnerOccupantUserResult,
    activeTabIndex,
    suiteDetailsComponents,
  };

  const handlersSlice: SuitePageHandlersSlice = {
    onSaveSuite: data.saveSuite,
    onDeleteUnit: data.deleteUnit,
    onReRenderKeyPickupPdf: data.reRenderKeyPickupPdf,
    onToggleMobileSync: data.toggleMobileSync,
    onFetchDetails,
    onEditSuite,
    onCloseOverlay: uiState.closeEditOverlay,
    onCloseMoreDetails: uiState.clearMoreDetails,
    fetchSuiteData: data.fetchSuiteData,
    refreshSuiteCounts: data.refreshSuiteCounts,
  };

  return {
    ...bootstrap,
    ...dataSlice,
    ...uiSlice,
    ...reduxSlice,
    ...contextSlice,
    ...derivationsSlice,
    ...handlersSlice,
  };
};
